import { Segmented, Skeleton } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../constants/routes.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import {
  COMPLETED_PROJECT_STATUS_LABELS,
  CustomerProjectCard
} from "./components/CustomerProjectCard.tsx"
import { PortalEmptyState } from "./components/PortalEmptyState.tsx"
import { PortalPageContainer } from "./components/PortalPageContainer.tsx"
import { PortalPageHeader } from "./components/PortalPageHeader.tsx"
import { isOutstanding } from "./helpers/invoices.ts"
import type { PortalProjectListItem } from "./types/portal.types.ts"

function PortalProjectsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [tab, setTab] = useState<"active" | "completed">("active")
  const [tabInitialized, setTabInitialized] = useState(false)

  const projects = useAppSelector((state) => state.portal.projects)
  const loaded = useAppSelector((state) => state.portal.projectsLoaded)
  const fetching = useAppSelector((state) => state.portal.projectsFetching)
  const crossProjectInvoices = useAppSelector(
    (state) => state.portal.crossProjectInvoices
  )

  const pendingByProject = useMemo(() => {
    const map = new Map<string, number>()
    crossProjectInvoices.filter(isOutstanding).forEach((invoice) => {
      map.set(invoice.project_id, (map.get(invoice.project_id) ?? 0) + 1)
    })
    return map
  }, [crossProjectInvoices])

  const { active, completed } = useMemo(() => {
    const a: PortalProjectListItem[] = []
    const c: PortalProjectListItem[] = []
    projects.forEach((project) => {
      if (
        typeof project.project_status === "string" &&
        COMPLETED_PROJECT_STATUS_LABELS.has(project.project_status)
      ) {
        c.push(project)
      } else {
        a.push(project)
      }
    })
    return { active: a, completed: c }
  }, [projects])

  // Default to Active — but only once the real counts are in: a customer
  // with no active projects at all (everything's Completed) would
  // otherwise always land on an empty Active tab first.
  useEffect(() => {
    if (tabInitialized || !loaded) return
    setTabInitialized(true)
    if (active.length === 0 && completed.length > 0) setTab("completed")
  }, [tabInitialized, loaded, active.length, completed.length])

  const visible = tab === "active" ? active : completed

  const openProject = (projectId: string) => {
    navigate(RoutesPaths.portal.project(projectId))
  }

  if (fetching && !loaded) {
    return (
      <PortalPageContainer>
        <Skeleton active paragraph={{ rows: 6 }} />
      </PortalPageContainer>
    )
  }

  if (loaded && projects.length === 0) {
    return (
      <PortalPageContainer>
        <PortalPageHeader title={t("portal.projects.title")} />
        <PortalEmptyState
          title={t("portal.home.noProjectsTitle")}
          description={t("portal.home.noProjectsDescription")}
        />
      </PortalPageContainer>
    )
  }

  return (
    <PortalPageContainer>
      <PortalPageHeader
        title={t("portal.projects.title")}
        actions={
          <Segmented
            value={tab}
            onChange={(value) => setTab(value as "active" | "completed")}
            options={[
              {
                label: t("portal.projects.tabActive", { count: active.length }),
                value: "active"
              },
              {
                label: t("portal.projects.tabCompleted", {
                  count: completed.length
                }),
                value: "completed"
              }
            ]}
          />
        }
      />

      {visible.length === 0 ? (
        <PortalEmptyState
          title={
            tab === "active"
              ? t("portal.projects.emptyActive")
              : t("portal.projects.emptyCompleted")
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {visible.map((project) => (
            <CustomerProjectCard
              key={project.id}
              project={project}
              pendingCount={pendingByProject.get(project.id) ?? 0}
              onOpen={() => openProject(project.id)}
            />
          ))}
        </div>
      )}
    </PortalPageContainer>
  )
}

export default PortalProjectsPage
